"use client";

import * as React from "react";
import { useState, useEffect, useCallback } from "react";
import { Bug, Send, Loader2, CheckCircle, Sparkles } from "lucide-react";
import { submitBugReport, type BugReportInput } from "@/app/actions/bug-report";
import { BugCategory } from "@prisma/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

const MAX_DESCRIPTION_LENGTH = 500;

// Category display labels
const categoryLabels: Record<BugCategory, string> = {
  BUG: "Bug",
  FEATURE_REQUEST: "Feature Request",
  UI_ISSUE: "UI Issue",
  PERFORMANCE: "Performance",
  SECURITY: "Security",
  OTHER: "Other",
};

// Category icons/descriptions for better UX
const categoryDescriptions: Record<BugCategory, string> = {
  BUG: "Something is broken or not working as expected",
  FEATURE_REQUEST: "Suggest a new feature or improvement",
  UI_ISSUE: "Visual or layout problems",
  PERFORMANCE: "Slow loading or laggy interactions",
  SECURITY: "Security concerns or vulnerabilities",
  OTHER: "Anything else",
};

interface BrowserInfo {
  userAgent: string;
  browser: string;
  os: string;
  deviceType: "mobile" | "tablet" | "desktop";
  screenWidth: number;
  screenHeight: number;
}

// Parse user agent to get browser info
function getBrowserInfo(): BrowserInfo {
  const ua = navigator.userAgent;
  let browser = "Unknown";
  let os = "Unknown";
  let deviceType: "mobile" | "tablet" | "desktop" = "desktop";

  // Detect browser
  if (ua.includes("Firefox/")) {
    browser = "Firefox";
  } else if (ua.includes("Edg/")) {
    browser = "Edge";
  } else if (ua.includes("Chrome/")) {
    browser = "Chrome";
  } else if (ua.includes("Safari/") && !ua.includes("Chrome/")) {
    browser = "Safari";
  } else if (ua.includes("Opera/") || ua.includes("OPR/")) {
    browser = "Opera";
  }

  // Detect OS
  if (ua.includes("Windows")) {
    os = "Windows";
  } else if (ua.includes("Mac OS")) {
    os = "macOS";
  } else if (ua.includes("Linux")) {
    os = "Linux";
  } else if (ua.includes("Android")) {
    os = "Android";
  } else if (ua.includes("iOS") || ua.includes("iPhone") || ua.includes("iPad")) {
    os = "iOS";
  }

  // Detect device type
  const width = window.innerWidth;
  if (width < 768) {
    deviceType = "mobile";
  } else if (width < 1024) {
    deviceType = "tablet";
  } else {
    deviceType = "desktop";
  }

  return {
    userAgent: ua,
    browser,
    os,
    deviceType,
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
  };
}

// Generate a session ID for anonymous tracking
function getSessionId(): string {
  const key = "usland_bug_session_id";
  let sessionId = sessionStorage.getItem(key);
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    sessionStorage.setItem(key, sessionId);
  }
  return sessionId;
}

// Confetti animation component
function SuccessConfetti() {
  const [particles, setParticles] = useState<Array<{ id: number; x: number; delay: number; color: string }>>([]);

  useEffect(() => {
    const colors = ["#10B981", "#34D399", "#6EE7B7", "#A7F3D0", "#D1FAE5"];
    const newParticles = Array.from({ length: 30 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      delay: Math.random() * 0.5,
      color: colors[Math.floor(Math.random() * colors.length)] as string,
    }));
    setParticles(newParticles);
  }, []);

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {particles.map((particle) => (
        <div
          key={particle.id}
          className="absolute w-2 h-2 rounded-full animate-confetti"
          style={{
            left: `${particle.x}%`,
            backgroundColor: particle.color,
            animationDelay: `${particle.delay}s`,
          }}
        />
      ))}
    </div>
  );
}

interface BugReportDialogProps {
  trigger?: React.ReactNode;
  className?: string;
}

export function BugReportDialog({ trigger, className }: BugReportDialogProps) {
  const [open, setOpen] = useState(false);
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState<BugCategory>("BUG");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const charactersRemaining = MAX_DESCRIPTION_LENGTH - description.length;
  const isDescriptionValid = description.trim().length > 0 && description.length <= MAX_DESCRIPTION_LENGTH;

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (!open) {
      // Small delay to allow close animation
      const timer = setTimeout(() => {
        setDescription("");
        setCategory("BUG");
        setIsSuccess(false);
        setError(null);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [open]);

  const handleSubmit = useCallback(async () => {
    if (!isDescriptionValid || isSubmitting) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const browserInfo = getBrowserInfo();
      const sessionId = getSessionId();

      const input: BugReportInput = {
        url: window.location.href,
        description: description.trim(),
        category,
        userAgent: browserInfo.userAgent,
        screenWidth: browserInfo.screenWidth,
        screenHeight: browserInfo.screenHeight,
        browser: browserInfo.browser,
        os: browserInfo.os,
        deviceType: browserInfo.deviceType,
        sessionId,
        metadata: {
          viewport: {
            width: window.innerWidth,
            height: window.innerHeight,
          },
          timestamp: new Date().toISOString(),
          referrer: document.referrer || null,
        },
      };

      const result = await submitBugReport(input);

      if (result.success) {
        setIsSuccess(true);
        // Auto-close after showing success
        setTimeout(() => {
          setOpen(false);
        }, 2000);
      } else {
        setError(result.error || "Failed to submit bug report. Please try again.");
      }
    } catch (err) {
      console.error("Bug report submission error:", err);
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }, [description, category, isDescriptionValid, isSubmitting]);

  // Handle keyboard submit
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && e.metaKey && isDescriptionValid && !isSubmitting) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit, isDescriptionValid, isSubmitting]
  );

  const defaultTrigger = (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={cn(
              "group relative h-10 w-10 rounded-lg",
              "bg-gradient-to-br from-red-500/10 to-orange-500/10",
              "hover:from-red-500/20 hover:to-orange-500/20",
              "border border-red-500/20 hover:border-red-500/40",
              "transition-all duration-300 ease-out",
              "hover:scale-105 hover:shadow-lg hover:shadow-red-500/10",
              className
            )}
            aria-label="Report a bug"
          >
            <Bug
              className={cn(
                "h-5 w-5 text-red-500",
                "transition-all duration-300",
                "group-hover:text-red-400 group-hover:scale-110"
              )}
            />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="right">
          <p>Report a Bug</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger || defaultTrigger}</DialogTrigger>
      <DialogContent
        className={cn(
          "sm:max-w-md",
          "!bg-gray-950 border-2 !border-blue-900"
        )}
      >
        {isSuccess ? (
          // Success state
          <div className="relative py-8 flex flex-col items-center justify-center gap-4">
            <SuccessConfetti />
            <div
              className={cn(
                "h-16 w-16 rounded-full",
                "bg-gradient-to-br from-green-500 to-emerald-600",
                "flex items-center justify-center",
                "animate-in zoom-in-50 duration-300"
              )}
            >
              <CheckCircle className="h-8 w-8 text-white" />
            </div>
            <div className="text-center space-y-2 animate-in fade-in-0 slide-in-from-bottom-4 duration-300 delay-150">
              <h3 className="text-lg font-semibold">Thank you!</h3>
              <p className="text-sm text-muted-foreground">
                Your feedback helps us improve USLand.
              </p>
            </div>
            <div className="flex items-center gap-1 text-xs text-muted-foreground animate-in fade-in-0 duration-300 delay-300">
              <Sparkles className="h-3 w-3" />
              <span>We appreciate you taking the time</span>
            </div>
          </div>
        ) : (
          // Form state
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Bug className="h-5 w-5 text-red-500" />
                Report an Issue
              </DialogTitle>
              <DialogDescription>
                Help us improve by reporting bugs or suggesting features.
                We read every submission.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-2" onKeyDown={handleKeyDown}>
              {/* Category Select */}
              <div className="space-y-2">
                <Label htmlFor="category">Category</Label>
                <Select
                  value={category}
                  onValueChange={(value) => setCategory(value as BugCategory)}
                  disabled={isSubmitting}
                >
                  <SelectTrigger id="category">
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(categoryLabels).map(([key, label]) => (
                      <SelectItem key={key} value={key}>
                        <div className="flex flex-col items-start">
                          <span>{label}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {categoryDescriptions[category]}
                </p>
              </div>

              {/* Description Textarea */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="description">Description</Label>
                  <span
                    className={cn(
                      "text-xs transition-colors",
                      charactersRemaining < 0
                        ? "text-red-500"
                        : charactersRemaining < 50
                        ? "text-yellow-500"
                        : "text-muted-foreground"
                    )}
                  >
                    {charactersRemaining} characters remaining
                  </span>
                </div>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Please describe what happened, what you expected, and steps to reproduce the issue..."
                  className={cn(
                    "min-h-[120px] resize-none",
                    "transition-all duration-200",
                    charactersRemaining < 0 && "border-red-500 focus:ring-red-500"
                  )}
                  disabled={isSubmitting}
                  autoFocus
                />
              </div>

              {/* Error message */}
              {error && (
                <div className="text-sm text-red-500 bg-red-500/10 border border-red-500/20 rounded-md p-3 animate-in fade-in-0 slide-in-from-top-1 duration-200">
                  {error}
                </div>
              )}

              {/* Auto-captured info hint */}
              <p className="text-xs text-muted-foreground">
                We automatically capture your current URL, browser, and device info to help debug issues.
              </p>
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={!isDescriptionValid || isSubmitting}
                className={cn(
                  "bg-gradient-to-r from-red-500 to-orange-500",
                  "hover:from-red-600 hover:to-orange-600",
                  "text-white shadow-md",
                  "transition-all duration-200",
                  "disabled:opacity-50 disabled:cursor-not-allowed"
                )}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="mr-2 h-4 w-4" />
                    Submit Report
                  </>
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default BugReportDialog;
